import { catchError, map, Observable, retry } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getBrand } from '../../utils/getBrand';
import { SafesendInfo } from '@ucx-js/chat-ui';
import { getChatApiBaseUrl } from '../../utils/getChatApiBaseUrl';
import { Language } from '../types/enum';

@Injectable({
  providedIn: 'root',
})
export class SafesendService {
  constructor(private http: HttpClient) {}

  sendSafeSendForm(
    safesendInfo: SafesendInfo[],
    chatId: string,
    sessionId: string,
    safeSendUrl: string
  ): Observable<any> {
    let url = getChatApiBaseUrl() + '/safesend/session/safesendInfo';
    if (safeSendUrl) {
      const urlParts = safeSendUrl.split('/');
      const liveChatSessionId = urlParts[urlParts.length - 1];
      url += '?sessionId=' + liveChatSessionId;
      return this.http.put(url, JSON.stringify(safesendInfo), {
        headers: {
          'Content-Type': 'application/json',
        },
      });
    }
    return this.http.post(url, JSON.stringify(safesendInfo), {
      headers: {
        'Content-Type': 'application/json',
        version: 'v1',
        vaid: sessionId,
        chatid: chatId ? chatId : '',
        brand: getBrand().name.toUpperCase(),
      },
    });
  }

  sendLoggedInUserSafeSend(
    sessionId: string,
    chatId: string,
    profileId: string,
    accountNumber: string,
    email: string,
    fullName: string
  ): Observable<any> {
    const headers = {
      brand: getBrand().name,
      chatId: chatId,
      profileId: profileId,
      version: 'v1',
      'content-type': 'application/json',
      vaid: sessionId,
    };
    return this.http.post(
      `${getChatApiBaseUrl()}/safesend/session/ssoInfo`,
      JSON.stringify({
        accountNumber: accountNumber,
        email: email,
        fullName: fullName,
      }),
      { headers, withCredentials: true }
    );
  }

  getProactiveSSFormTemplate(
    departmentId: string,
    language: string = Language.en
  ): Observable<any> {
    const templateAPI =
      getChatApiBaseUrl() + '/safesend/template?departmentId=' + departmentId;
    return this.http
      .get(templateAPI, {
        headers: { isProactive: 'true', language },
      })
      .pipe(
        retry(2),
        catchError(() => {
          return this.getDefaultSafeSendTemplate(language);
        }),
        map((res) => this.convertSSFormTemplate(res?.questionnaire))
      );
  }

  getDefaultSafeSendTemplate(language: string = Language.en): Observable<any> {
    const templateAPI = `configurations/default-safesendtemplates/${
      getBrand().name
    }/default-${language}.json`;
    return this.http.get<any>(templateAPI);
  }

  getQuestionnaire(safeSendUrl: string): Observable<any> {
    const urlParts = safeSendUrl.split('/');
    const liveChatSessionId = urlParts[urlParts.length - 1];
    const url =
      getChatApiBaseUrl() +
      '/safesend//questionnaire?sessionId=' +
      liveChatSessionId;
    return this.http.get(url).pipe(retry(2), map(this.convertSSFormTemplate));
  }

  convertSSFormTemplate(questionnaire) {
    try {
      const fields =
        questionnaire
          ?.map((i) => {
            if (!i) {
              return;
            }
            const {
              question: name,
              placeholder,
              questionId: id,
              mask,
              type,
              required,
              questionType,
            } = i;
            return {
              type: type
                ? type
                : questionType == 'credit'
                ? questionType
                : 'text',
              name,
              placeholder,
              label: name,
              mask,
              value: '',
              required,
              id,
              questionType,
            };
          })
          .filter((i) => i && Object.keys(i).length > 0) || [];
      const sortedFields = [
        ...fields.filter((item) => !item.questionType),
        ...fields.filter((item) => item.questionType == 'special'),
        ...fields.filter((item) => item.questionType == 'custom'),
        ...fields.filter((item) => item.questionType == 'bank'),
        ...fields.filter((item) => item.questionType == 'credit'),
      ];
      return {
        questionnaire: sortedFields,
      };
    } catch (e) {
      return {
        questionnaire: [],
      };
    }
  }
}
